/* eslint-disable max-len */
import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext, useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQuery } from '../hooks/useQuery';

import useApiCall from '../hooks/useApiCall';
import useLocalState from '../hooks/useLocalState';
import authService from '../services/authService';
import changeFavicon from '../utils/changeFavicon';
import { AuthProviderProps, CompaniesSelectOptions, MakeLoginProps, SelectedCompanyType, UserDataType } from './types';

export interface AppContextInterface {
  isLoading: boolean;
  signed: boolean;
  signOut: () => void;
  Login: ({ loginType, login, password }: MakeLoginProps) => void;
  changeCompany: ({ value, label }: { value: number, label: string }) => void;
  token: string;
  validationTk: string;
  userData: UserDataType;
  selectedCompany: { value: number, label: string };
  handleSelectedCompanyChange: (companyObject: { value: number, label: string }) => void;
  companiesList: CompaniesSelectOptions[];
  setCompaniesList: Dispatch<SetStateAction<CompaniesSelectOptions[]>>;
  isDirectLogin: boolean;
  logoUrl: string;
  isDifferentSubdomain: boolean;
}

const AuthContext = createContext({});

export function useAppContext(): AppContextInterface | any {
  const context = useContext(AuthContext);

  return context;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [signed, setSigned] = useState(false);
  const [signedLocalStorage, setSignedLocalStorage] = useLocalState('signed');

  const [validationTk, setValidationTk] = useState('');
  const [validationTkLocalStorage, setValidationTkLocalStorage] = useLocalState('validationTk');

  const [token, setToken] = useState('');
  const [tokenLocalStorage, setTokenLocalStorage] = useLocalState('token');

  const [logoUrl, setLogoUrl] = useState('');
  const [logoUrlLocalStorage, setLogoUrlLocalStorage] = useLocalState('logoUrl');

  const [userData, setUserData] = useState<UserDataType>({} as UserDataType);
  const [userDataLocalStorage, setUserDataLocalStorage] = useLocalState('userData');

  const [selectedCompany, setSelectedCompany] = useState({} as SelectedCompanyType);
  const [selectedCompanyLocalStorage, setSelectedCompanyLocalStorage] = useLocalState('companyData');

  const [companiesList, setCompaniesList] = useState<CompaniesSelectOptions[]>([]);
  const [signOutRedirect, setSignOutRedirect] = useState('');

  const [hasLoggedInDirect, setHasLoggedInDirect] = useState(false);
  const [isDifferentSubdomain, setIsDifferentSubdomain] = useState(false);
  const [isDifferentSubdomainLocalStorage, setIsDifferentSubdomainLocalStorage] = useLocalState('isDifferentSubdomain');

  const query = useQuery();
  const mustLoginDirect = query.has('tkLogin');
  const navigate = useNavigate();
  const { apiCall } = useApiCall();

  const page = query.get('page');

  const handleSelectedCompanyChange = useCallback((companyObject: { value: number, label: string }) => {
    setSelectedCompany(companyObject);
    setSelectedCompanyLocalStorage(companyObject);
  }, [setSelectedCompanyLocalStorage]);

  const getValidationToken = useCallback(async () => {
    await apiCall({
      apiToCall: authService.getValidationTk,
      isValidationToken: true,
      isFirstTokenAPi: true,
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        if (response.codigo == 0) {
          toast.error(`Não foi possível validar as credenciais. Por favor, recarregue a página (${response.msg})`);
          return;
        }
        const validationToken = response.tkValida;
        setValidationTk(validationToken);
        setValidationTkLocalStorage(validationToken);
      }
    });
  }, [apiCall, setValidationTkLocalStorage]);

  const makeDirectLogin = useCallback(async () => {
    // if (token || tokenLocalStorage) {
    //   navigate('/');
    //   return;
    // }

    await apiCall({
      apiToCall: authService.directLogin,
      isValidationToken: true,
      isLoginApi: true,
      validationToken: decodeURIComponent(query.get('tkLogin') || ''),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível realizar o login. Por favor, tente novamente',
      catchAction: () => setSigned(false),
      actionAfterResponse: (response) => {
        if (!response.empLiberada && response.dadosUsu) {
          toast.warn('Seu cadastro já foi realizado. Por favor, aguarde para que nossa equipe libere o seu acesso.');
          return false;
        }
        if (!response.dadosUsu.acessoValidado) {
          navigate(`/verification?codUsu=${response.dadosUsu.codUsu}&perfilUsu=${response.dadosUsu.perfilUsu}&codEmpresa=${response.dadosUsu.codEmpresa}&sendCode`);
          return false;
        }
        if (response.codigo === 0) {
          toast.error('Usuário/senha inválidos');
          return false;
        }

        const { dadosUsu, permissoesNav, trocaLogo, urlLogo } = response;

        if (!dadosUsu || !permissoesNav) {
          toast.error('Houve um erro ao tentar realizar o login. Por favor, tente novamente.');
          return false;
        }

        let isSubdomainCapta = true;

        if (trocaLogo) {
          setLogoUrl(urlLogo);
          setLogoUrlLocalStorage(urlLogo);

          const hostname = window.location.hostname;
          const subdomain = hostname.split('.').length > 2 ? hostname.split('.')[0] : null;
          isSubdomainCapta = !!subdomain?.includes('app');

          if (!isSubdomainCapta) {
            document.title = subdomain?.toUpperCase() || 'Captatec SISMOB';

            const isPluxee = subdomain?.includes('pluxee');
            changeFavicon(isPluxee ? '/faviconPluxee.ico' : urlLogo);
          }
        }

        setIsDifferentSubdomain(!isSubdomainCapta);
        setIsDifferentSubdomainLocalStorage(!isSubdomainCapta);

        setSigned(true);
        setSignedLocalStorage(true);

        setToken(response.tk);
        setTokenLocalStorage(response.tk);

        setUserData({
          userCode: dadosUsu?.codUsu,
          userProfile: dadosUsu?.perfilUsu,
          userName: dadosUsu?.usuario,
          companyCode: dadosUsu?.codEmpresa,
          companyName: dadosUsu?.empresa,
          companyLabel: dadosUsu?.empresa,
          permissions: permissoesNav,
          services: response?.servicosContratados,
          page,
        });
        setUserDataLocalStorage({
          userCode: dadosUsu?.codUsu,
          userProfile: dadosUsu?.perfilUsu,
          userName: dadosUsu?.usuario,
          companyCode: dadosUsu?.codEmpresa,
          companyName: dadosUsu?.empresa,
          companyLabel: dadosUsu?.empresa,
          permissions: permissoesNav,
          services: response?.servicosContratados,
          page,
        });

        setSelectedCompany({
          value: response.dadosUsu.codEmpresa,
          label: response.dadosUsu.empresa,
        });
        setSelectedCompanyLocalStorage({
          value: response.dadosUsu.codEmpresa,
          label: response.dadosUsu.empresa,
        });
        setSignOutRedirect(response.linkSignout);
        setHasLoggedInDirect(true);

        toast(`Seja bem vindo ao sismob, ${response?.dadosUsu?.usuario || ''}!`, { icon: <>👋</> });
        navigate('/');
        return true;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, setSelectedCompanyLocalStorage, setTokenLocalStorage, setUserDataLocalStorage]);

  const makeLogin = useCallback(async ({ loginType, login, password }: MakeLoginProps) => {
    if (loginType === 'username') {
      let canProceed = true;

      await apiCall({
        apiToCall: authService.loginWithUsername,
        reqBody: JSON.stringify({
          login,
          senha: password,
        }),
        onStartLoad: () => setIsLoading(true),
        onEndLoad: () => setIsLoading(false),
        catchMessage: 'Não foi possível realizar o login. Por favor, tente novamente',
        catchAction: () => setSigned(false),
        actionAfterResponse: (response) => {
          if (response.codigo === 0) {
            canProceed = false;
            toast.error('Usuário/senha inválidos');
            setIsLoading(false);
            return false;
          }
          if (!response.cadastrar) {
            canProceed = false;
            navigate(`/updateRegister?codUsu=${response.codUsu}&perfilUsu=${response.perfilUsu}&codEmpresa=${response.codEmpresa}`);
            toast.info('Por favor, atualize seu cadastro em nosso sistema para prosseguir!');
            setIsLoading(false);
            return false;
          }
          if (response.validarAcesso) {
            canProceed = false;
            navigate(`/verification?codUsu=${response.codUsu}&perfilUsu=${response.perfilUsu}&codEmpresa=${response.codEmpresa}&sendCode`);
            setIsLoading(false);
            return false;
          }
          canProceed = true;
        },
        isValidationToken: true,
        isLoginApi: true,
        validationToken: validationTk,
      });

      if (!canProceed) return;

      await apiCall({
        apiToCall: authService.login,
        isValidationToken: true,
        isLoginApi: true,
        validationToken: validationTk,
        reqBody: JSON.stringify({
          cpf: '',
          login,
          senha: password,
          tipoDado: 3,
        }),
        onStartLoad: () => setIsLoading(true),
        onEndLoad: () => setIsLoading(false),
        catchMessage: 'Não foi possível realizar o login. Por favor, tente novamente',
        catchAction: () => setSigned(false),
        actionAfterResponse: (response) => {
          if (!response.empLiberada && response.dadosUsu) {
            toast.warn('Seu cadastro já foi realizado. Por favor, aguarde para que nossa equipe libere o seu acesso.');
            return false;
          }
          if (!response.dadosUsu?.acessoValidado) {
            navigate(`/verification?codUsu=${response?.dadosUsu.codUsu}&perfilUsu=${response?.dadosUsu.perfilUsu}&codEmpresa=${response?.dadosUsu.codEmpresa}&sendCode`);
            return false;
          }
          if (response.codigo === 0) {
            toast.error('Usuário/senha inválidos');
            return false;
          }

          const { dadosUsu, permissoesNav } = response;

          if (!dadosUsu || !permissoesNav) {
            toast.error('Houve um erro ao tentar realizar o login. Por favor, tente novamente.');
            return false;
          }

          setToken(response.tk);
          setTokenLocalStorage(response.tk);

          setUserData({
            userCode: dadosUsu?.codUsu,
            userProfile: dadosUsu?.perfilUsu,
            userName: dadosUsu?.usuario,
            companyCode: dadosUsu?.codEmpresa,
            companyName: dadosUsu?.empresa,
            companyLabel: dadosUsu?.empresa,
            permissions: permissoesNav,
            services: response?.servicosContratados,
            page,
          });
          setUserDataLocalStorage({
            userCode: dadosUsu?.codUsu,
            userProfile: dadosUsu?.perfilUsu,
            userName: dadosUsu?.usuario,
            companyCode: dadosUsu?.codEmpresa,
            companyName: dadosUsu?.empresa,
            companyLabel: dadosUsu?.empresa,
            permissions: permissoesNav,
            services: response?.servicosContratados,
            page,
          });

          setSelectedCompany({
            value: response?.dadosUsu.codEmpresa,
            label: response?.dadosUsu.empresa,
          });
          setSelectedCompanyLocalStorage({
            value: response?.dadosUsu.codEmpresa,
            label: response?.dadosUsu.empresa,
          });

          setSigned(true);
          setSignedLocalStorage(true);
          toast(`Seja bem vindo ao sismob, ${response?.dadosUsu?.usuario || ''}!`, { icon: <>👋</> });

          navigate('/');
          return true;
        }
      });
    } else {
      await apiCall({
        apiToCall: authService.login,
        reqBody: JSON.stringify({
          cpf: login,
          login: '',
          senha: password,
          tipoDado: 1,
        }),
        isValidationToken: true,
        isLoginApi: true,
        validationToken: validationTk,
        onStartLoad: () => setIsLoading(true),
        onEndLoad: () => setIsLoading(false),
        catchMessage: 'Não foi possível realizar o login. Por favor, tente novamente',
        catchAction: () => setSigned(false),
        actionAfterResponse: (response) => {
          if (!response.empLiberada && response.dadosUsu) {
            toast.warn('Seu cadastro já foi realizado. Por favor, aguarde para que nossa equipe libere o seu acesso.');
            return false;
          }
          if (!response.dadosUsu.acessoValidado) {
            navigate(`/verification?codUsu=${response.dadosUsu.codUsu}&perfilUsu=${response.dadosUsu.perfilUsu}&codEmpresa=${response.dadosUsu.codEmpresa}&sendCode`);
            return false;
          }
          if (response.codigo === 0) {
            toast.error('Usuário/senha inválidos');
            return false;
          }

          const { dadosUsu, permissoesNav, trocaLogo, urlLogo } = response;

          if (!dadosUsu || !permissoesNav) {
            toast.error('Houve um erro ao tentar realizar o login. Por favor, tente novamente.');
            return false;
          }

          if (trocaLogo) {
            setLogoUrl(urlLogo);
            setLogoUrlLocalStorage(urlLogo);
          }

          // let isSubdomainCapta = true;
          // if (trocaLogo) {
          //   setLogoUrl(urlLogo);
          //   setLogoUrlLocalStorage(urlLogo);

          //   const hostname = window.location.hostname;
          //   const subdomain = hostname.split('.').length > 2 ? hostname.split('.')[0] : null;
          //   isSubdomainCapta = !!subdomain?.includes('app');

          //   if (!isSubdomainCapta) {
          //     document.title = subdomain?.toUpperCase() || 'Captatec SISMOB';
          //     changeFavicon(urlLogo);
          //   }
          // }
          // setIsDifferentSubdomain(!isSubdomainCapta);
          // setIsDifferentSubdomainLocalStorage(!isSubdomainCapta);

          setSigned(true);
          setSignedLocalStorage(true);

          setToken(response.tk);
          setTokenLocalStorage(response.tk);

          setUserData({
            userCode: dadosUsu?.codUsu,
            userProfile: dadosUsu?.perfilUsu,
            userName: dadosUsu?.usuario,
            companyCode: dadosUsu?.codEmpresa,
            companyName: dadosUsu?.empresa,
            companyLabel: dadosUsu?.empresa,
            permissions: permissoesNav,
            services: response?.servicosContratados,
            page,
          });
          setUserDataLocalStorage({
            userCode: dadosUsu?.codUsu,
            userProfile: dadosUsu?.perfilUsu,
            userName: dadosUsu?.usuario,
            companyCode: dadosUsu?.codEmpresa,
            companyName: dadosUsu?.empresa,
            companyLabel: dadosUsu?.empresa,
            permissions: permissoesNav,
            services: response?.servicosContratados,
            page,
          });

          setSelectedCompany({
            value: response.dadosUsu.codEmpresa,
            label: response.dadosUsu.empresa,
          });
          setSelectedCompanyLocalStorage({
            value: response.dadosUsu.codEmpresa,
            label: response.dadosUsu.empresa,
          });
          setHasLoggedInDirect(false);
          setSignOutRedirect('');

          toast(`Seja bem vindo ao sismob, ${response?.dadosUsu?.usuario || ''}!`, { icon: <>👋</> });
          navigate('/');
          return true;
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, setSelectedCompanyLocalStorage, setTokenLocalStorage, setUserDataLocalStorage, validationTk]);

  const changeCompany = useCallback(({ value, label }: { value: number, label: string }) => {
    setSelectedCompany({ value, label });
    setSelectedCompanyLocalStorage({ value, label });
  }, [setSelectedCompanyLocalStorage]);

  const signOut = useCallback(() => {
    setIsLoading(true);
    toast.error('Sessão encerrada, realize o login novamente', {
      position: 'bottom-center',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'dark',
      toastId: 'session-expired',
    });

    const themeLocalStorage = localStorage.getItem('theme');
    localStorage.clear();
    if (themeLocalStorage) {
      localStorage.setItem('theme', themeLocalStorage);
    }

    setSignedLocalStorage(false);
    setTokenLocalStorage('');
    setUserDataLocalStorage({});
    setSelectedCompanyLocalStorage({});

    setSigned(false);
    setToken('');
    setUserData({} as UserDataType);
    setSelectedCompany({} as SelectedCompanyType);
    setCompaniesList([]);
    setLogoUrl('');
    setLogoUrlLocalStorage('');
    navigate('/');
    if (signOutRedirect) {
      const hostname = window.location.hostname;
      const subdomain = hostname.split('.').length > 2 ? hostname.split('.')[0] : null;
      const isPluxee = subdomain?.includes('pluxee');
      if (isPluxee) {
        window.close();
      }
      window.location.href = signOutRedirect;
    }
    setTimeout(() => setIsLoading(false), 2000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, setSelectedCompanyLocalStorage, setTokenLocalStorage, setUserDataLocalStorage, setSignedLocalStorage, token, tokenLocalStorage, userData, userDataLocalStorage, signOutRedirect]);

  useEffect(() => {
    if (mustLoginDirect) {
      makeDirectLogin();
      return;
    }
    getValidationToken();
  }, [getValidationToken, makeDirectLogin, mustLoginDirect]);

  const appData = useMemo((): AppContextInterface => ({
    isLoading,
    signed: signedLocalStorage || signed,
    signOut,
    Login: makeLogin,
    changeCompany,
    token: tokenLocalStorage ?? token,
    validationTk: validationTkLocalStorage || validationTk,
    userData: userDataLocalStorage || userDataLocalStorage,
    selectedCompany: selectedCompanyLocalStorage || selectedCompany,
    handleSelectedCompanyChange,
    companiesList,
    setCompaniesList,
    isDirectLogin: hasLoggedInDirect,
    logoUrl: logoUrlLocalStorage || logoUrl || '',
    isDifferentSubdomain: isDifferentSubdomainLocalStorage || isDifferentSubdomain,
  }), [isLoading, signedLocalStorage, signed, signOut, makeLogin, changeCompany, tokenLocalStorage, token, validationTkLocalStorage, validationTk, userDataLocalStorage, selectedCompanyLocalStorage, selectedCompany, handleSelectedCompanyChange, companiesList, hasLoggedInDirect, logoUrlLocalStorage, logoUrl, isDifferentSubdomainLocalStorage, isDifferentSubdomain]);

  return (
    <AuthContext.Provider value={appData}>
      {children}
    </AuthContext.Provider>
  );
}
